/* Admin · Money Mission Control (`/admin/payments-ops`)
 *
 * Single consolidated console for everything money movement: card pulls,
 * ACH/RTP/wire payouts, internal ledger transfers, fraud signals,
 * reconciliation drift, chargebacks, and treasury limits.
 *
 * Sits ON TOP of the existing `/admin/wallets` page (per-seller balance
 * view) and `/admin/integrations/tabapay` page (provider config) — this
 * page is the ops-time view across both axes.
 *
 * 5 tabs: Transactions · Fraud Signals · Reconciliation · Chargebacks · Alerts & Limits
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  ShieldAlert, ScanLine, Activity, AlertTriangle, ScrollText,
  CreditCard, Banknote, Zap, ArrowLeftRight, Loader2, RefreshCw,
  Search, Lock, Unlock, ListChecks, Flame, Snowflake, ChevronRight,
  CheckCircle2, XCircle, Clock, FileWarning,
} from "lucide-react";


function money(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}
function fmtAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ago = (Date.now() - d.getTime()) / 1000;
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    return d.toLocaleString();
  } catch { return iso; }
}

const RAIL_ICONS = {
  card:   CreditCard,
  ach:    Banknote,
  rtp:    Zap,
  wire:   ArrowLeftRight,
  ledger: ScrollText,
};
const RAIL_COLORS = {
  card:   "text-violet-600 bg-violet-50 border-violet-200",
  ach:    "text-sky-700 bg-sky-50 border-sky-200",
  rtp:    "text-amber-700 bg-amber-50 border-amber-200",
  wire:   "text-emerald-700 bg-emerald-50 border-emerald-200",
  ledger: "text-slate-700 bg-slate-100 border-slate-200",
};
const STATUS_COLORS = {
  completed: "text-emerald-700 bg-emerald-50",
  settled:   "text-emerald-700 bg-emerald-50",
  posted:    "text-emerald-700 bg-emerald-50",
  pending:   "text-amber-700 bg-amber-50",
  failed:    "text-rose-700 bg-rose-50",
  returned:  "text-rose-700 bg-rose-50",
  reversed:  "text-rose-700 bg-rose-50",
};
const SEVERITY_COLORS = {
  high:   "bg-rose-50 border-rose-200 text-rose-900",
  medium: "bg-amber-50 border-amber-200 text-amber-900",
  low:    "bg-sky-50 border-sky-200 text-sky-900",
};

const TABS = [
  { id: "transactions", label: "Transactions",      icon: Activity },
  { id: "fraud",        label: "Fraud Signals",     icon: ShieldAlert },
  { id: "recon",        label: "Reconciliation",    icon: ScanLine },
  { id: "chargebacks",  label: "Chargebacks",       icon: FileWarning },
  { id: "alerts",       label: "Alerts & Limits",   icon: AlertTriangle },
];


export default function AdminPaymentsOps() {
  const [tab, setTab] = useState("transactions");
  return (
    <div data-testid="admin-payments-ops" className="space-y-6">
      <PageHeader
        title="Money Mission Control"
        subtitle="Transactions · fraud signals · reconciliation · chargebacks · treasury limits — every TabaPay rail in one console."
      />

      <div data-testid="payments-ops-tabs" className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`pops-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon size={15}/>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "transactions" && <TransactionsTab/>}
      {tab === "fraud"        && <FraudTab/>}
      {tab === "recon"        && <ReconTab/>}
      {tab === "chargebacks"  && <ChargebacksTab/>}
      {tab === "alerts"       && <AlertsTab/>}
    </div>
  );
}


/* ─── Transactions tab ──────────────────────────────────────────────── */
function TransactionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rail, setRail] = useState("all");
  const [status, setStatus] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [sinceDays, setSinceDays] = useState(7);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        since_days: String(sinceDays),
        limit: "100",
      });
      if (rail && rail !== "all") params.set("rail", rail);
      if (status) params.set("status", status);
      if (userEmail.trim()) params.set("user_email", userEmail.trim());
      const r = await api.get(`/admin/payments-ops/transactions?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load transactions");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rail, status, sinceDays]);

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="txn-kpis">
        <KpiTile label="Transactions"  value={data?.kpis?.total_count ?? "—"} icon={Activity}/>
        <KpiTile label="Volume"        value={data ? money(data.kpis.total_volume) : "—"} icon={Banknote}/>
        <KpiTile label="Failures"      value={data?.kpis?.failures_count ?? "—"} icon={XCircle} tone="rose"/>
        <KpiTile label="Card txns"     value={data?.by_rail?.card ?? data?.by_rail?.tabapay ?? 0} icon={CreditCard}/>
        <KpiTile label="ACH/RTP/wire"  value={(data?.by_rail?.ach || 0) + (data?.by_rail?.rtp || 0) + (data?.by_rail?.wire || 0)} icon={Zap}/>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid="txn-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400"/>
          <Input
            data-testid="txn-filter-email"
            placeholder="User email …"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Select value={rail} onValueChange={setRail}>
          <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="txn-filter-rail">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rails</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="ach">ACH</SelectItem>
            <SelectItem value="rtp">RTP</SelectItem>
            <SelectItem value="wire">Wire</SelectItem>
            <SelectItem value="ledger">Ledger</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="txn-filter-status">
            <SelectValue placeholder="Any status"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="reversed">Reversed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(Number(v))}>
          <SelectTrigger className="w-[120px] h-9 text-sm" data-testid="txn-filter-since">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7d</SelectItem>
            <SelectItem value="30">30d</SelectItem>
            <SelectItem value="90">90d</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="txn-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading transactions…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="txn-empty">No transactions match your filters.</div>
        ) : (
          <table className="w-full text-sm" data-testid="txn-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">User</th>
                <th className="text-left px-4 py-2 font-medium">Rail</th>
                <th className="text-left px-4 py-2 font-medium">Type</th>
                <th className="text-right px-4 py-2 font-medium">Amount</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Ref</th>
                <th className="px-2 py-2"/>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const RailIcon = RAIL_ICONS[r.rail] || ScrollText;
                return (
                  <tr key={r.id}
                      data-testid={`txn-row-${r.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelected(r)}>
                    <td className="px-4 py-2 text-slate-600 tabular-nums">{fmtAt(r.created_at)}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800 truncate max-w-[200px]">{r.user_email || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{r.user_company || r.user_role}</div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${RAIL_COLORS[r.rail] || RAIL_COLORS.ledger}`}>
                        <RailIcon size={11}/> {r.rail}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-[12px]">{r.type}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{money(r.amount)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${STATUS_COLORS[r.status] || "text-slate-700 bg-slate-100"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 mono text-[11px] text-slate-500">{r.ref || r.tabapay_transaction_id?.slice(0,8) || "—"}</td>
                    <td className="px-2 py-2 text-slate-400"><ChevronRight size={14}/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && <TimelineDrawer txn={selected} onClose={() => setSelected(null)} onRefresh={load}/>}
    </div>
  );
}


/* ─── Transaction timeline drawer ──────────────────────────────────── */
function TimelineDrawer({ txn, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/payments-ops/transactions/${txn.id}/timeline`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load timeline");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [txn.id]);

  const handlePoll = async () => {
    const reason = window.prompt("Reason (≥3 chars):") || "";
    if (reason.trim().length < 3) return;
    setPolling(true);
    try {
      const r = await api.post(`/admin/payments-ops/transactions/${txn.id}/poll-status`, { reason });
      toast.success(r.data.drift
        ? `Drift! Updated ${txn.status} → ${r.data.new_status}`
        : `In sync · TabaPay says ${r.data.tabapay_status}`);
      onRefresh && onRefresh();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Poll failed");
    } finally { setPolling(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="txn-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity size={16}/> Transaction · {txn.id?.slice(0, 8)}…
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Full life trace · ledger entries + TabaPay API calls + webhook events + chargebacks.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Loading…</div>
        ) : data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3" data-testid="txn-summary">
              <Field label="User"    value={`${data.user?.email || "—"} (${data.user?.role || "—"})`}/>
              <Field label="Amount"  value={money(data.wallet_txn.amount)}/>
              <Field label="Type"    value={data.wallet_txn.type}/>
              <Field label="Status"  value={data.wallet_txn.status} mono/>
              <Field label="Method"  value={data.wallet_txn.method || "ledger"}/>
              <Field label="TabaPay ID" value={data.tabapay_transaction_id || "—"} mono small/>
            </div>

            {data.invoice && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-[12px] text-sky-900">
                <div className="font-semibold">Linked invoice: {data.invoice.code || data.invoice.id?.slice(0,8)}</div>
                <div>Status: {data.invoice.status} · Total: {money(data.invoice.total)}</div>
              </div>
            )}

            <div data-testid="txn-timeline-list" className="space-y-2">
              {data.timeline.length === 0 ? (
                <div className="text-slate-500 text-sm">No events recorded yet.</div>
              ) : data.timeline.map((evt, i) => (
                <div key={i} data-testid={`timeline-row-${evt.kind}`}
                     className="flex items-start gap-3 border border-slate-100 rounded-lg p-2.5 bg-white">
                  <TimelineDot kind={evt.kind}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-slate-800">{evt.summary}</div>
                    <div className="text-[11px] text-slate-500 flex gap-2 items-center">
                      <span>{fmtAt(evt.at)}</span>
                      <span>·</span>
                      <span className="mono">{evt.kind}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {data.chargebacks && data.chargebacks.length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="txn-chargebacks">
                <div className="font-semibold text-rose-900 text-[13px]">
                  Chargebacks ({data.chargebacks.length})
                </div>
                {data.chargebacks.map((cb) => (
                  <div key={cb.id} className="text-[12px] text-rose-800 mt-1">
                    · {cb.reason} · status: <span className="mono">{cb.status}</span> · opened {fmtAt(cb.opened_at)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {data?.tabapay_transaction_id && (
            <Button data-testid="txn-poll-btn" variant="outline" onClick={handlePoll} disabled={polling}>
              {polling ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <RefreshCw size={14} className="mr-1.5"/>}
              Poll TabaPay
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Fraud Signals tab ─────────────────────────────────────────────── */
function FraudTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("active_7d");
  const [freezeUser, setFreezeUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payments-ops/fraud-signals?scope=${scope}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load fraud signals");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fraud-kpis">
        <KpiTile label="Total signals" value={data?.total ?? "—"} icon={ShieldAlert} tone="rose"/>
        <KpiTile label="High severity" value={data?.by_severity?.high ?? 0} icon={Flame} tone="rose"/>
        <KpiTile label="Medium severity" value={data?.by_severity?.medium ?? 0} icon={AlertTriangle} tone="amber"/>
        <KpiTile label="Distinct rule kinds" value={Object.keys(data?.by_kind || {}).length} icon={ListChecks}/>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid="fraud-filters">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="fraud-scope">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active_7d">Active users (last 7d)</SelectItem>
            <SelectItem value="all_users">All users</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="fraud-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Re-scan
        </Button>
        <div className="ml-auto text-[11px] text-slate-500">
          Last scanned {data?.scanned_at ? fmtAt(data.scanned_at) : "—"}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Scanning…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="fraud-empty">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2"/>
            No fraud signals detected. Everything looks healthy.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="fraud-rows">
            {data.rows.map((s, i) => (
              <div key={i} data-testid={`fraud-row-${s.kind}-${i}`}
                   className={`p-3 border-l-4 ${SEVERITY_COLORS[s.severity]} flex items-start gap-3`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                      s.severity === "high" ? "bg-rose-600 text-white" :
                      s.severity === "medium" ? "bg-amber-500 text-white" : "bg-sky-500 text-white"
                    }`}>{s.severity}</span>
                    <span className="font-mono text-[11px] text-slate-600">{s.kind}</span>
                  </div>
                  <div className="font-medium text-[13px] mt-1">{s.summary}</div>
                  <div className="text-[11.5px] text-slate-700 mt-0.5">
                    {s.user_email || s.user_id || "platform"} · {fmtAt(s.detected_at)}
                  </div>
                </div>
                {s.user_id && s.action_hint === "freeze_money_movement" && (
                  <Button
                    data-testid={`fraud-freeze-btn-${s.user_id}`}
                    variant="destructive" size="sm"
                    onClick={() => setFreezeUser(s)}
                  >
                    <Lock size={14} className="mr-1"/> Freeze
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {freezeUser && <FreezeDialog signal={freezeUser} onClose={() => setFreezeUser(null)} onDone={load}/>}
    </div>
  );
}


function FreezeDialog({ signal, onClose, onDone }) {
  const [reason, setReason] = useState(`Auto-flagged by ${signal.kind}: ${signal.summary}`);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/payments-ops/freeze-user/${signal.user_id}`, { reason });
      toast.success("User money movement frozen · sessions revoked · cards locked");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freeze failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="freeze-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Snowflake size={16}/> Freeze money movement
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Suspends the user account, locks every card on file, and revokes active sessions. Reversible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-[12px]">
            <div className="font-semibold text-rose-900">{signal.summary}</div>
            <div className="text-rose-700 mt-1">User: <span className="mono">{signal.user_email || signal.user_id}</span></div>
          </div>
          <div className="text-[12px] text-slate-600">
            This will: <strong>suspend the account</strong>, <strong>freeze every card on file</strong>,
            and <strong>revoke all active sessions</strong>. Reversible via the Unfreeze action.
          </div>
          <Textarea
            data-testid="freeze-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (≥3 chars)"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            data-testid="freeze-submit"
            variant="destructive"
            onClick={submit}
            disabled={busy || reason.trim().length < 3}
          >
            {busy ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <Lock size={14} className="mr-1.5"/>}
            Freeze user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Reconciliation tab ────────────────────────────────────────────── */
function ReconTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(72);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payments-ops/reconciliation?since_hours=${hours}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reconciliation");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-kpis">
        <KpiTile label="Drift rows"    value={data?.drift_rows?.length ?? "—"} icon={ScanLine}
                 tone={data?.drift_rows?.length > 0 ? "rose" : "emerald"}/>
        <KpiTile label="Ledger rows"   value={data?.scanned?.ledger_count ?? "—"} icon={ScrollText}/>
        <KpiTile label="Webhook events" value={data?.scanned?.webhook_count ?? "—"} icon={Activity}/>
        <KpiTile label="API calls"     value={data?.scanned?.api_log_count ?? "—"} icon={ArrowLeftRight}/>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="recon-window">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24">Last 24h</SelectItem>
            <SelectItem value="72">Last 3 days</SelectItem>
            <SelectItem value="168">Last 7 days</SelectItem>
            <SelectItem value="720">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="recon-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Re-scan
        </Button>
        <div className="ml-auto text-[11px] text-slate-500">
          Last scanned {data?.scanned_at ? fmtAt(data.scanned_at) : "—"}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Scanning…</div>
        ) : !data || data.drift_rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="recon-empty">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2"/>
            <div className="font-semibold text-slate-700">No drift detected</div>
            <div className="text-[12px] mt-1">Our ledger matches TabaPay's webhook record over the last {hours}h.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="recon-rows">
            {data.drift_rows.map((r, i) => (
              <div key={i} data-testid={`recon-row-${r.kind}-${i}`} className={`p-3 ${SEVERITY_COLORS[r.severity] || "bg-white"} border-l-4`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-slate-100">
                    {r.kind.replace(/_/g, " ")}
                  </span>
                  <span className="font-mono text-[11px] text-slate-600">txn: {r.txn_id?.slice(0,12)}</span>
                </div>
                <div className="text-[13px] font-medium">{r.summary}</div>
                {r.ledger_state && (
                  <div className="text-[11.5px] text-slate-700 mt-1">
                    Ledger: <span className="mono">{r.ledger_state.status}</span> ·
                    user <span className="mono">{r.ledger_state.user_id?.slice(0,8)}</span> ·
                    {money(r.ledger_state.amount)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── Chargebacks tab ───────────────────────────────────────────────── */
function ChargebacksTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const r = await api.get(`/admin/payments-ops/chargebacks?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load chargebacks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cb-kpis">
        <KpiTile label="Open"     value={data?.by_status?.open ?? 0}    icon={FileWarning} tone="amber"/>
        <KpiTile label="Responded" value={data?.by_status?.responded ?? 0} icon={Activity}/>
        <KpiTile label="Won"      value={data?.by_status?.won ?? 0}     icon={CheckCircle2} tone="emerald"/>
        <KpiTile label="Lost"     value={data?.by_status?.lost ?? 0}    icon={XCircle} tone="rose"/>
        <KpiTile label="Expired"  value={data?.by_status?.expired ?? 0} icon={Clock} tone="slate"/>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="cb-filter-status">
            <SelectValue placeholder="Any status"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="responded">Responded</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="cb-refresh" variant="outline" size="sm" onClick={load}>
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Loading…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="cb-empty">No chargebacks recorded.</div>
        ) : (
          <table className="w-full text-sm" data-testid="cb-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Opened</th>
                <th className="text-left px-4 py-2 font-medium">User</th>
                <th className="text-left px-4 py-2 font-medium">Reason</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Reference</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((cb) => (
                <tr key={cb.id} data-testid={`cb-row-${cb.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-600">{fmtAt(cb.opened_at)}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{cb.user_email || "—"}</div>
                    <div className="text-[11px] text-slate-500">{cb.user_company || ""}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{cb.reason}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${
                      cb.status === "won" ? "bg-emerald-50 text-emerald-700" :
                      cb.status === "lost" ? "bg-rose-50 text-rose-700" :
                      cb.status === "responded" ? "bg-sky-50 text-sky-700" :
                      "bg-amber-50 text-amber-700"
                    }`}>{cb.status}</span>
                  </td>
                  <td className="px-4 py-2 mono text-[11px] text-slate-500">{cb.reference_id?.slice(0,12) || "—"}</td>
                  <td className="px-2 py-2">
                    <Button data-testid={`cb-edit-${cb.id}`} size="sm" variant="outline" onClick={() => setEditing(cb)}>Manage</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <ChargebackEditDialog cb={editing} onClose={() => setEditing(null)} onDone={load}/>}
    </div>
  );
}


function ChargebackEditDialog({ cb, onClose, onDone }) {
  const [status, setStatus] = useState(cb.status || "open");
  const [evidence, setEvidence] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = status !== cb.status && reason.trim().length >= 3;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.patch(`/admin/payments-ops/chargebacks/${cb.id}`, {
        status, evidence_note: evidence || undefined, reason,
      });
      toast.success(`Chargeback marked ${status}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="cb-edit-dialog">
        <DialogHeader>
          <DialogTitle>Update chargeback</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Mark this case as responded / won / lost / expired with an optional evidence note.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[12px]">
            <div><strong>Reason:</strong> {cb.reason}</div>
            <div><strong>Opened:</strong> {fmtAt(cb.opened_at)}</div>
            <div><strong>Reference:</strong> <span className="mono">{cb.reference_id}</span></div>
          </div>
          <div>
            <label className="text-[12px] font-medium text-slate-700 block mb-1">New status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="cb-edit-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea
            data-testid="cb-edit-evidence"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="Evidence / response note (optional)"
            rows={3}
          />
          <Textarea
            data-testid="cb-edit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for this status change (≥3 chars)"
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="cb-edit-submit" onClick={submit} disabled={!canSubmit || busy}>
            {busy ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <CheckCircle2 size={14} className="mr-1.5"/>}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Alerts & Limits tab ──────────────────────────────────────────── */
function AlertsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payments-ops/alerts-limits");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load alerts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>;

  const headroom = data.treasury.headroom_pct;
  const headroomTone = headroom === null ? "slate" : headroom < 20 ? "rose" : headroom < 50 ? "amber" : "emerald";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="alerts-tiles">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Treasury 24h</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{money(data.treasury.txn_24h_volume)}</div>
          <div className="text-[11px] text-slate-500">{data.treasury.txn_24h_count} transactions</div>
          {data.treasury.daily_limit_usd ? (
            <>
              <div className="mt-3 h-2 bg-slate-100 rounded overflow-hidden">
                <div className={`h-full ${
                  headroomTone === "rose" ? "bg-rose-500" :
                  headroomTone === "amber" ? "bg-amber-500" : "bg-emerald-500"
                }`} style={{ width: `${100 - (headroom ?? 100)}%` }}/>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Limit: {money(data.treasury.daily_limit_usd)} · Headroom: {headroom?.toFixed(0)}%
              </div>
            </>
          ) : (
            <div className="text-[11px] text-slate-500 mt-2">Set <span className="mono">TABAPAY_DAILY_LIMIT_USD</span> to see headroom</div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="webhook-health">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Webhook health (7d)</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{data.webhook_health.events_7d}</div>
          <div className="text-[11px] text-slate-500">events received</div>
          <div className="mt-2 flex gap-3 text-[11px]">
            <span className={data.webhook_health.bad_signature_7d > 0 ? "text-rose-700 font-medium" : "text-slate-500"}>
              {data.webhook_health.bad_signature_7d} bad-sig
            </span>
            <span className={data.webhook_health.unapplied_7d > 0 ? "text-amber-700 font-medium" : "text-slate-500"}>
              {data.webhook_health.unapplied_7d} unapplied
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="api-health">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">TabaPay API (24h)</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{data.api_health.calls_24h}</div>
          <div className="text-[11px] text-slate-500">outbound calls</div>
          <div className="mt-2 flex gap-3 text-[11px]">
            <span className={data.api_health.errors_24h > 0 ? "text-rose-700 font-medium" : "text-slate-500"}>
              {data.api_health.errors_24h} errors ({data.api_health.error_pct}%)
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="alerts-frozen">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Snowflake size={16} className={data.frozen_users > 0 ? "text-rose-600" : "text-slate-500"}/>
            <div className="font-semibold text-slate-800">{data.frozen_users} users currently money-frozen</div>
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14}/></Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="alerts-platform">
        <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <AlertTriangle size={15}/> Platform alerts (last 30d · {data.alerts.length})
        </div>
        {data.alerts.length === 0 ? (
          <div className="text-slate-500 text-[12.5px]">No platform alerts.</div>
        ) : (
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {data.alerts.map((a, i) => (
              <div key={i} data-testid={`alert-row-${i}`}
                   className="text-[12px] border-l-2 border-amber-300 bg-amber-50 px-3 py-1.5 rounded-r">
                <div className="font-medium text-amber-900">{a.kind || "alert"}</div>
                <div className="text-[11px] text-amber-700">{fmtAt(a.at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── Small components ─────────────────────────────────────────────── */
function KpiTile({ label, value, icon: Icon, tone = "slate" }) {
  const TONES = {
    slate:   "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${TONES[tone] || TONES.slate}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-70">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className="text-xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value, mono, small }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${mono ? "mono" : ""} ${small ? "text-[11px]" : "text-[13px]"} font-medium text-slate-800 break-all`}>
        {String(value || "—")}
      </div>
    </div>
  );
}

function TimelineDot({ kind }) {
  const m = {
    ledger_created:    { c: "bg-slate-500", Icon: ScrollText },
    tabapay_api_call:  { c: "bg-violet-500", Icon: ArrowLeftRight },
    tabapay_webhook:   { c: "bg-sky-500", Icon: Activity },
    chargeback:        { c: "bg-rose-500", Icon: FileWarning },
    ledger_settled:    { c: "bg-emerald-500", Icon: CheckCircle2 },
    ledger_failed:     { c: "bg-rose-500", Icon: XCircle },
  }[kind] || { c: "bg-slate-400", Icon: ChevronRight };
  const Icon = m.Icon;
  return (
    <div className={`shrink-0 w-7 h-7 rounded-full ${m.c} text-white flex items-center justify-center`}>
      <Icon size={13}/>
    </div>
  );
}
